import { apiInstance } from "@/api/base";
import { baseApi } from "@/api/baseApi";
import { FILES_TAGS } from "@/api/files.constraints";
import { originalDocumentDownloadUrl } from "@/views/drive/components/downloadOriginalDocument";
import { DOCUMENT_AUTHORING_BASE_URL, DOCUMENT_AUTHORING_TAGS, DOCX_MIME_TYPE } from "./documentAuthoring.constraints";
import type {
  AuthoringDocument,
  AuthoringRun,
  CreateRunRequest,
  CreateRunResponse,
  SaveVersionConflict,
  SaveVersionRequest,
  SaveVersionResponse,
  EligibilityCheckResponse,
} from "./documentAuthoring.types";

const BASE = DOCUMENT_AUTHORING_BASE_URL;

const documentTag = (fseId: string) => ({ type: DOCUMENT_AUTHORING_TAGS.AUTHORING_DOCUMENT_TAG, id: fseId }) as const;
const runTag = (runId: string) => ({ type: DOCUMENT_AUTHORING_TAGS.AUTHORING_RUN_TAG, id: runId }) as const;

const docxBlob = (bytes: Uint8Array) => new Blob([bytes as BlobPart], { type: DOCX_MIME_TYPE });

// Downloads live in the document sub-app so nginx's `/files/` X-Accel path applies (§12.3).
export const versionDocxUrl = (fseId: string, versionId: string): string =>
  `/files/authoring/version-docx?fse_id=${encodeURIComponent(fseId)}&version_id=${encodeURIComponent(versionId)}`;

export const runDocxUrl = (runId: string): string => `/files/authoring/run-docx?run_id=${encodeURIComponent(runId)}`;

export const headDocxUrl = (fseId: string): string => originalDocumentDownloadUrl(fseId);

/** Fetches a docx through the session-authenticated axios instance (401 handling included). */
export const fetchDocxBytes = async (url: string): Promise<Uint8Array> => {
  const response = await apiInstance.get<ArrayBuffer>(url, { responseType: "arraybuffer" });
  return new Uint8Array(response.data);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Narrows an RTK Query error to the 409 merge-conflict body. */
export const asSaveVersionConflict = (error: unknown): SaveVersionConflict | null => {
  if (!isRecord(error) || error.status !== 409 || !isRecord(error.data)) return null;
  const data = error.data;
  if (!Array.isArray(data.conflicts) || !isRecord(data.head)) return null;
  return data as unknown as SaveVersionConflict;
};

/** The backend's user-safe `{detail}` text, or a generic fallback. Never surfaces raw server output. */
export const authoringErrorMessage = (error: unknown, fallback: string): string => {
  if (isRecord(error) && isRecord(error.data) && typeof error.data.detail === "string") {
    return error.data.detail;
  }
  if (isRecord(error) && error.status === 429) return "Too many runs started recently. Please wait and try again.";
  if (isRecord(error) && error.status === 413) return "The document is too large to process.";
  return fallback;
};

export const documentAuthoringApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getAuthoringDocument: builder.query<AuthoringDocument, string>({
      providesTags: (_result, _error, fseId) => [documentTag(fseId)],
      query: fseId => ({ url: `${BASE}/documents/${encodeURIComponent(fseId)}` }),
    }),
    saveVersion: builder.mutation<SaveVersionResponse, SaveVersionRequest>({
      invalidatesTags: (_result, _error, { fseId }) => [documentTag(fseId), FILES_TAGS.FILES_TAG],
      query: ({ fseId, file, fileName, base_version_id, note, resolutions, idempotency_key }) => {
        const formData = new FormData();
        formData.append("file", docxBlob(file), fileName);
        formData.append("base_version_id", base_version_id);
        if (note) formData.append("note", note);
        if (resolutions) formData.append("resolutions", JSON.stringify(resolutions));
        formData.append("idempotency_key", idempotency_key);
        return {
          url: `${BASE}/documents/${encodeURIComponent(fseId)}/versions`,
          method: "POST",
          headers: { "Content-Type": "multipart/form-data" },
          data: formData,
        };
      },
    }),
    createRun: builder.mutation<CreateRunResponse, CreateRunRequest>({
      query: ({ fseId, file, fileName, kind, instructions, template_id }) => {
        const formData = new FormData();
        formData.append("file", docxBlob(file), fileName);
        formData.append("kind", kind);
        if (kind === "instructions" && instructions !== undefined) formData.append("instructions", instructions);
        if (kind === "template" && template_id !== undefined) formData.append("template_id", template_id);
        return {
          url: `${BASE}/documents/${encodeURIComponent(fseId)}/runs`,
          method: "POST",
          headers: { "Content-Type": "multipart/form-data" },
          data: formData,
        };
      },
    }),
    getRun: builder.query<AuthoringRun, string>({
      providesTags: (_result, _error, runId) => [runTag(runId)],
      query: runId => ({ url: `${BASE}/runs/${encodeURIComponent(runId)}` }),
    }),
    cancelRun: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, runId) => [runTag(runId)],
      query: runId => ({ url: `${BASE}/runs/${encodeURIComponent(runId)}/cancel`, method: "POST" }),
    }),
  }),
});

export const {
  useGetAuthoringDocumentQuery,
  useSaveVersionMutation,
  useCreateRunMutation,
  useGetRunQuery,
  useLazyGetRunQuery,
  useCancelRunMutation,
} = documentAuthoringApi;

/**
 * Self-service migration for a .docx uploaded before the feature: the backend checks the stored
 * original and flips `authoring_eligible`. A plain request (no RTK hook) so drive rows stay
 * renderable without the store; the list catches up on its next refetch.
 */
export const checkAuthoringEligibility = async (fseId: string): Promise<EligibilityCheckResponse> => {
  const response = await apiInstance.post<EligibilityCheckResponse>(
    `${BASE}/documents/${encodeURIComponent(fseId)}/eligibility`,
  );
  return response.data;
};
